import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import SoundManager from '../utils/SoundManager';
import { disposeScene } from '../utils/ThreeCleanup';

// Interfaces
interface Choice {
  id: string;
  text: string;
}

interface Question {
  id: string;
  text: string;
  choices: Choice[];
}

interface DungeonState {
  currentQuestionIndex: number;
  selectedChoices: string[];
  phase: 'intro' | 'question' | 'transition' | 'conclusion';
  transitionProgress: number;
}

// TEMPLE OF WISDOM theme
const Dungeon2Visualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const cleanupFnsRef = useRef<(() => void)[]>([]);

  const [dungeonState, setDungeonState] = useState<DungeonState>({
    currentQuestionIndex: -1,
    selectedChoices: [],
    phase: 'intro',
    transitionProgress: 0
  });

  const [displayText, setDisplayText] = useState<string>('');
  const [showChoices, setShowChoices] = useState<boolean>(false);
  const [conclusion, setConclusion] = useState<boolean>(false);

  const textAnimationRef = useRef<number | null>(null);
  const typingPositionRef = useRef<number>(0);
  const targetTextRef = useRef<string>('');

  const questions: Question[] = [
    {
      id: 'truth',
      text: "What is the nature of truth?",
      choices: [
        { id: 'objective', text: "Objective" },
        { id: 'subjective', text: "Subjective" },
        { id: 'constructed', text: "Constructed" }
      ]
    },
    {
      id: 'will',
      text: "Does free will exist?",
      choices: [
        { id: 'absolutely', text: "Absolutely" },
        { id: 'illusion', text: "It's an illusion" },
        { id: 'moments', text: "Only in moments" }
      ]
    },
    {
      id: 'death',
      text: "What survives death?",
      choices: [
        { id: 'nothing', text: "Nothing" },
        { id: 'ideas', text: "Ideas" },
        { id: 'energy', text: "Energy" }
      ]
    },
    {
      id: 'suffering',
      text: "Is suffering necessary?",
      choices: [
        { id: 'growth', text: "For growth" },
        { id: 'never', text: "Never" },
        { id: 'chosen', text: "Only if chosen" }
      ]
    },
    {
      id: 'virtue',
      text: "What is the highest virtue?",
      choices: [
        { id: 'courage', text: "Courage" },
        { id: 'compassion', text: "Compassion" },
        { id: 'wisdom', text: "Wisdom" }
      ]
    }
  ];

  // Gold/amber/warm color scheme
  const GOLD = 0xffd700;
  const AMBER = 0xffbf00;
  const WARM_ORANGE = 0xff8c00;

  const animateText = (text: string, callback?: () => void) => {
    if (textAnimationRef.current !== null) {
      cancelAnimationFrame(textAnimationRef.current);
      textAnimationRef.current = null;
    }
    setDisplayText('');
    typingPositionRef.current = 0;
    targetTextRef.current = text;

    const typeNextChar = () => {
      if (typingPositionRef.current < targetTextRef.current.length) {
        const nextPosition = Math.min(typingPositionRef.current + 1, targetTextRef.current.length);
        const currentText = targetTextRef.current.substring(0, nextPosition);
        setDisplayText(currentText);
        if (typingPositionRef.current % 5 === 0) {
          SoundManager.playSound('keypress');
        }
        typingPositionRef.current = nextPosition;
        textAnimationRef.current = requestAnimationFrame(() => {
          setTimeout(typeNextChar, 70);
        });
      } else {
        textAnimationRef.current = null;
        if (callback) callback();
      }
    };
    textAnimationRef.current = requestAnimationFrame(typeNextChar);
  };

  const getChoiceColor = (index: number) => {
    const colors = [GOLD, AMBER, WARM_ORANGE];
    return colors[index % colors.length];
  };

  const selectChoice = (questionIndex: number, choiceIndex: number) => {
    SoundManager.playSound('command');
    const selectedChoice = questions[questionIndex].choices[choiceIndex].id;
    setDungeonState(prev => ({
      ...prev,
      selectedChoices: [...prev.selectedChoices, selectedChoice],
      phase: 'transition'
    }));
    setShowChoices(false);
    if (sceneRef.current) {
      createTransitionScene(sceneRef.current);
    }
  };

  // Floating candle helper
  const createCandle = (scene: THREE.Scene, position: THREE.Vector3) => {
    // Candle body
    const candleGeo = new THREE.CylinderGeometry(0.08, 0.08, 0.5, 8);
    const candleMat = new THREE.MeshPhongMaterial({ color: 0xf5e6c8 });
    const candle = new THREE.Mesh(candleGeo, candleMat);
    candle.position.copy(position);
    scene.add(candle);

    // Flame (small sphere)
    const flameGeo = new THREE.SphereGeometry(0.06, 8, 8);
    const flameMat = new THREE.MeshBasicMaterial({
      color: GOLD,
      transparent: true,
      opacity: 0.9
    });
    const flame = new THREE.Mesh(flameGeo, flameMat);
    flame.position.copy(position);
    flame.position.y += 0.3;
    scene.add(flame);

    // Light
    const light = new THREE.PointLight(AMBER, 0.5, 5);
    light.position.copy(position);
    light.position.y += 0.4;
    scene.add(light);

    const animateCandle = () => {
      if (!candle.parent) return;
      const time = Date.now() * 0.001;
      const offset = position.x * 1.3 + position.z * 0.7;
      candle.position.y = position.y + Math.sin(time * 0.8 + offset) * 0.15;
      flame.position.y = candle.position.y + 0.3;
      light.position.y = candle.position.y + 0.4;
      flame.scale.setScalar(0.8 + Math.random() * 0.4);
      light.intensity = 0.4 + Math.random() * 0.2;
      requestAnimationFrame(animateCandle);
    };
    animateCandle();
  };

  // Intro scene: Ancient temple entrance
  const createIntroScene = (scene: THREE.Scene) => {
    // Stone floor
    const floorGeo = new THREE.PlaneGeometry(30, 30);
    const floorMat = new THREE.MeshPhongMaterial({ color: 0x4a3728, shininess: 10 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -2;
    scene.add(floor);

    // Stone pillars (large, ancient)
    const pillarGeo = new THREE.CylinderGeometry(1, 1.2, 12, 12);
    const pillarMat = new THREE.MeshPhongMaterial({ color: 0x6b5b4a, specular: 0x222211, shininess: 5 });
    const pillarPositions = [[-5, 4, -3], [5, 4, -3], [-5, 4, 3], [5, 4, 3]];
    pillarPositions.forEach(pos => {
      const pillar = new THREE.Mesh(pillarGeo, pillarMat);
      pillar.position.set(pos[0], pos[1], pos[2]);
      scene.add(pillar);

      // Pillar capital (top decoration)
      const capGeo = new THREE.CylinderGeometry(1.4, 1, 0.5, 12);
      const cap = new THREE.Mesh(capGeo, pillarMat);
      cap.position.set(pos[0], pos[1] + 6.2, pos[2]);
      scene.add(cap);
    });

    // Temple gate (arch)
    const archGeo = new THREE.TorusGeometry(3, 0.5, 8, 16, Math.PI);
    const archMat = new THREE.MeshPhongMaterial({ color: 0x8b7355 });
    const arch = new THREE.Mesh(archGeo, archMat);
    arch.position.set(0, 5, -3);
    scene.add(arch);

    // Floating candles
    for (let i = 0; i < 20; i++) {
      const x = (Math.random() - 0.5) * 12;
      const y = Math.random() * 4 + 1;
      const z = (Math.random() - 0.5) * 10;
      createCandle(scene, new THREE.Vector3(x, y, z));
    }

    // Mystical fog particles
    const fogCount = 800;
    const fogGeo = new THREE.BufferGeometry();
    const fogPos = new Float32Array(fogCount * 3);
    for (let i = 0; i < fogCount * 3; i += 3) {
      fogPos[i] = (Math.random() - 0.5) * 25;
      fogPos[i + 1] = Math.random() * 2 - 2;
      fogPos[i + 2] = (Math.random() - 0.5) * 20;
    }
    fogGeo.setAttribute('position', new THREE.BufferAttribute(fogPos, 3));
    const fogMat = new THREE.PointsMaterial({
      color: 0xffd700,
      size: 0.08,
      transparent: true,
      opacity: 0.2,
      blending: THREE.AdditiveBlending
    });
    const fog = new THREE.Points(fogGeo, fogMat);
    scene.add(fog);

    const animateFog = () => {
      if (!fog.parent) return;
      const positions = fog.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < positions.length; i += 3) {
        positions[i] += (Math.random() - 0.5) * 0.015;
        positions[i + 1] += (Math.random() - 0.5) * 0.01;
      }
      fog.geometry.attributes.position.needsUpdate = true;
      requestAnimationFrame(animateFog);
    };
    animateFog();

    // Central altar
    const altarGeo = new THREE.BoxGeometry(2, 1, 2);
    const altarMat = new THREE.MeshPhongMaterial({ color: 0x5a4a3a, specular: 0x332211, shininess: 15 });
    const altar = new THREE.Mesh(altarGeo, altarMat);
    altar.position.set(0, -1.5, 0);
    scene.add(altar);

    // Glowing symbol on altar
    const symbolGeo = new THREE.RingGeometry(0.3, 0.5, 32);
    const symbolMat = new THREE.MeshBasicMaterial({
      color: GOLD,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide
    });
    const symbol = new THREE.Mesh(symbolGeo, symbolMat);
    symbol.rotation.x = -Math.PI / 2;
    symbol.position.set(0, -0.99, 0);
    scene.add(symbol);

    const animateSymbol = () => {
      if (!symbol.parent) return;
      const time = Date.now() * 0.001;
      if (symbol.material instanceof THREE.MeshBasicMaterial) {
        symbol.material.opacity = 0.5 + Math.sin(time * 2) * 0.3;
      }
      symbol.rotation.z += 0.005;
      requestAnimationFrame(animateSymbol);
    };
    animateSymbol();
  };

  // Question scene
  const createQuestionScene = (scene: THREE.Scene, questionIndex: number) => {
    while (scene.children.length > 2) {
      scene.remove(scene.children[0]);
    }

    // Stone floor
    const floorGeo = new THREE.PlaneGeometry(30, 30);
    const floorMat = new THREE.MeshPhongMaterial({ color: 0x3d2e1f, shininess: 8 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -3;
    scene.add(floor);

    // Surrounding stone pillars
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const radius = 10;
      const pillarGeo = new THREE.CylinderGeometry(0.7, 0.9, 10, 10);
      const pillarMat = new THREE.MeshPhongMaterial({ color: 0x6b5b4a, shininess: 5 });
      const pillar = new THREE.Mesh(pillarGeo, pillarMat);
      pillar.position.set(Math.cos(angle) * radius, 2, Math.sin(angle) * radius);
      scene.add(pillar);

      // Torch-like light on each pillar
      const torchLight = new THREE.PointLight(AMBER, 0.6, 8);
      torchLight.position.set(Math.cos(angle) * radius, 7, Math.sin(angle) * radius);
      scene.add(torchLight);
    }

    // Floating candles
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      const r = 4 + Math.random() * 3;
      createCandle(scene, new THREE.Vector3(
        Math.cos(angle) * r,
        1 + Math.random() * 3,
        Math.sin(angle) * r
      ));
    }

    // Choice runes (golden tablets)
    const question = questions[questionIndex];
    question.choices.forEach((_choice, index) => {
      const angle = ((index / question.choices.length) * Math.PI * 2) + Math.PI / 6;
      const radius = 5;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      // Stone tablet
      const tabletGeo = new THREE.BoxGeometry(1.8, 2.2, 0.2);
      const tabletMat = new THREE.MeshPhongMaterial({
        color: 0x8b7355,
        specular: 0x443322,
        shininess: 20
      });
      const tablet = new THREE.Mesh(tabletGeo, tabletMat);
      tablet.position.set(x, 0, z);
      tablet.rotation.y = -angle + Math.PI / 2;
      (tablet as any).choiceIndex = index;
      scene.add(tablet);

      // Glowing rune on tablet face
      const runeGeo = new THREE.CircleGeometry(0.6, 6);
      const runeMat = new THREE.MeshBasicMaterial({
        color: getChoiceColor(index),
        transparent: true,
        opacity: 0.8,
        side: THREE.DoubleSide
      });
      const rune = new THREE.Mesh(runeGeo, runeMat);
      rune.position.set(x, 0, z);
      rune.rotation.y = -angle + Math.PI / 2;
      rune.position.x += Math.cos(-angle + Math.PI / 2) * 0.12;
      rune.position.z += Math.sin(-angle + Math.PI / 2) * 0.12;
      scene.add(rune);

      const pointLight = new THREE.PointLight(getChoiceColor(index), 0.8, 5);
      pointLight.position.set(x, 0, z);
      scene.add(pointLight);

      // Float animation
      const animateTablet = () => {
        if (!tablet.parent) return;
        const time = Date.now() * 0.001;
        tablet.position.y = Math.sin(time + index * 2) * 0.2;
        rune.position.y = tablet.position.y;
        if (runeMat instanceof THREE.MeshBasicMaterial) {
          runeMat.opacity = 0.5 + Math.sin(time * 1.5 + index) * 0.3;
        }
        requestAnimationFrame(animateTablet);
      };
      animateTablet();
    });

    // Mystical particles
    const pCount = 600;
    const pGeo = new THREE.BufferGeometry();
    const pPos = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount * 3; i += 3) {
      pPos[i] = (Math.random() - 0.5) * 30;
      pPos[i + 1] = Math.random() * 12 - 3;
      pPos[i + 2] = (Math.random() - 0.5) * 30;
    }
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    const pMat = new THREE.PointsMaterial({
      color: GOLD,
      size: 0.04,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending
    });
    const particles = new THREE.Points(pGeo, pMat);
    scene.add(particles);

    // Raycaster
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const handleMouseMove = (event: MouseEvent) => {
      if (!containerRef.current || !cameraRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / containerRef.current.clientWidth) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / containerRef.current.clientHeight) * 2 + 1;
      raycaster.setFromCamera(mouse, cameraRef.current);
      const intersects = raycaster.intersectObjects(scene.children);

      scene.children.forEach(child => {
        if (child instanceof THREE.Mesh && 'choiceIndex' in child) {
          child.scale.set(1, 1, 1);
        }
      });

      if (intersects.length > 0) {
        const object = intersects[0].object;
        if (object instanceof THREE.Mesh && 'choiceIndex' in object) {
          object.scale.set(1.2, 1.2, 1.2);
        }
      }
    };

    const handleClick = () => {
      if (!cameraRef.current) return;
      raycaster.setFromCamera(mouse, cameraRef.current);
      const intersects = raycaster.intersectObjects(scene.children);
      if (intersects.length > 0) {
        const object = intersects[0].object;
        if (object instanceof THREE.Mesh && 'choiceIndex' in object) {
          selectChoice(questionIndex, (object as any).choiceIndex);
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);
    cleanupFnsRef.current.push(() => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
    });
  };

  // Transition: spiral staircase descent
  const createTransitionScene = (scene: THREE.Scene) => {
    while (scene.children.length > 2) {
      scene.remove(scene.children[0]);
    }

    // Spiral staircase path
    const spiralPoints: THREE.Vector3[] = [];
    for (let i = 0; i < 60; i++) {
      const t = i / 60;
      const angle = t * Math.PI * 6; // 3 full rotations
      const radius = 3;
      spiralPoints.push(new THREE.Vector3(
        Math.cos(angle) * radius,
        -t * 30, // descend
        Math.sin(angle) * radius
      ));
    }
    const spiralPath = new THREE.CatmullRomCurve3(spiralPoints);

    // Steps along the spiral
    for (let i = 0; i < 40; i++) {
      const t = i / 40;
      const point = spiralPath.getPoint(t);
      const stepGeo = new THREE.BoxGeometry(2, 0.2, 1);
      const stepMat = new THREE.MeshPhongMaterial({
        color: 0x6b5b4a,
        shininess: 10
      });
      const step = new THREE.Mesh(stepGeo, stepMat);
      step.position.copy(point);
      const tangent = spiralPath.getTangent(t);
      step.lookAt(point.clone().add(tangent));
      scene.add(step);
    }

    // Central column
    const columnGeo = new THREE.CylinderGeometry(0.5, 0.5, 35, 12);
    const columnMat = new THREE.MeshPhongMaterial({ color: 0x5a4a3a });
    const column = new THREE.Mesh(columnGeo, columnMat);
    column.position.y = -15;
    scene.add(column);

    // Candle lights along the way
    for (let i = 0; i < 15; i++) {
      const t = i / 15;
      const point = spiralPath.getPoint(t);
      const light = new THREE.PointLight(AMBER, 0.5, 6);
      light.position.set(point.x * 1.5, point.y, point.z * 1.5);
      scene.add(light);
    }

    // Warm particles
    const pCount = 800;
    const pGeo = new THREE.BufferGeometry();
    const pPos = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount * 3; i += 3) {
      const t = Math.random();
      const pos = spiralPath.getPoint(t);
      pPos[i] = pos.x + (Math.random() - 0.5) * 4;
      pPos[i + 1] = pos.y + (Math.random() - 0.5) * 2;
      pPos[i + 2] = pos.z + (Math.random() - 0.5) * 4;
    }
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    const pMat = new THREE.PointsMaterial({
      color: GOLD,
      size: 0.06,
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending
    });
    scene.add(new THREE.Points(pGeo, pMat));

    if (cameraRef.current) {
      cameraRef.current.position.copy(spiralPath.getPoint(0));
      cameraRef.current.lookAt(spiralPath.getPoint(0.05));
    }

    let progress = 0;
    const animateTransition = () => {
      if (!cameraRef.current) return;
      progress += 0.005;
      setDungeonState(prev => ({ ...prev, transitionProgress: Math.min(1, progress) }));

      if (progress <= 1) {
        const pos = spiralPath.getPoint(progress);
        cameraRef.current.position.copy(pos);
        const lookAt = spiralPath.getPoint(Math.min(1, progress + 0.05));
        cameraRef.current.lookAt(lookAt);
        requestAnimationFrame(animateTransition);
      } else {
        if (dungeonState.currentQuestionIndex >= questions.length - 1) {
          startConclusion();
        } else {
          setDungeonState(prev => ({
            ...prev,
            currentQuestionIndex: prev.currentQuestionIndex + 1,
            phase: 'question',
            transitionProgress: 0
          }));
        }
      }
    };
    animateTransition();
  };

  const startConclusion = () => {
    setDungeonState(prev => ({ ...prev, phase: 'conclusion' }));
    if (sceneRef.current) {
      createConclusionScene(sceneRef.current);
    }
  };

  const createConclusionScene = (scene: THREE.Scene) => {
    while (scene.children.length > 2) {
      scene.remove(scene.children[0]);
    }

    // Empty temple chamber
    const floorGeo = new THREE.CircleGeometry(12, 32);
    const floorMat = new THREE.MeshPhongMaterial({ color: 0x4a3728, shininess: 10 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -2;
    scene.add(floor);

    // Tall pillars forming a circle
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      const r = 8;
      const pillarGeo = new THREE.CylinderGeometry(0.5, 0.6, 14, 10);
      const pillarMat = new THREE.MeshPhongMaterial({ color: 0x6b5b4a });
      const pillar = new THREE.Mesh(pillarGeo, pillarMat);
      pillar.position.set(Math.cos(angle) * r, 5, Math.sin(angle) * r);
      scene.add(pillar);
    }

    // Central golden flame (but empty at center)
    const flameGeo = new THREE.ConeGeometry(0.5, 2, 8);
    const flameMat = new THREE.MeshBasicMaterial({
      color: GOLD,
      transparent: true,
      opacity: 0.6
    });
    const flame = new THREE.Mesh(flameGeo, flameMat);
    flame.position.y = 1;
    scene.add(flame);

    const flameLight = new THREE.PointLight(GOLD, 1.5, 15);
    flameLight.position.set(0, 2, 0);
    scene.add(flameLight);

    // Many floating candles going upward
    for (let i = 0; i < 30; i++) {
      const x = (Math.random() - 0.5) * 14;
      const y = Math.random() * 8;
      const z = (Math.random() - 0.5) * 14;
      createCandle(scene, new THREE.Vector3(x, y, z));
    }

    // Dust motes
    const dustCount = 400;
    const dustGeo = new THREE.BufferGeometry();
    const dustPos = new Float32Array(dustCount * 3);
    for (let i = 0; i < dustCount * 3; i += 3) {
      dustPos[i] = (Math.random() - 0.5) * 18;
      dustPos[i + 1] = Math.random() * 10 - 2;
      dustPos[i + 2] = (Math.random() - 0.5) * 18;
    }
    dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
    const dustMat = new THREE.PointsMaterial({
      color: AMBER,
      size: 0.04,
      transparent: true,
      opacity: 0.3,
      blending: THREE.AdditiveBlending
    });
    scene.add(new THREE.Points(dustGeo, dustMat));

    const animateFlame = () => {
      if (!flame.parent) return;
      const time = Date.now() * 0.001;
      flame.scale.set(
        1 + Math.sin(time * 3) * 0.2,
        1 + Math.sin(time * 2.5) * 0.1,
        1 + Math.sin(time * 3) * 0.2
      );
      flameLight.intensity = 1 + Math.sin(time * 4) * 0.5;
      requestAnimationFrame(animateFlame);
    };
    animateFlame();

    if (cameraRef.current) {
      cameraRef.current.position.set(0, 4, 12);
      cameraRef.current.lookAt(0, 2, 0);
    }

    // Fade out flame then show text
    setTimeout(() => {
      const startTime = Date.now();
      const duration = 4000;
      const fadeFlame = () => {
        const elapsed = Date.now() - startTime;
        const p = Math.min(1, elapsed / duration);
        if (flameMat instanceof THREE.MeshBasicMaterial) flameMat.opacity = 0.6 * (1 - p);
        flameLight.intensity = 1.5 * (1 - p);
        flame.scale.setScalar(1 - p * 0.8);
        if (p < 1) requestAnimationFrame(fadeFlame);
      };
      fadeFlame();

      setTimeout(() => {
        setConclusion(true);
        animateText(
          "THE TEMPLE WAS EMPTY ALL ALONG.\n\nTHE WISDOM WAS IN THE WALKING.",
          () => { SoundManager.playSound('command'); }
        );
      }, duration + 1000);
    }, 3000);
  };

  const startSequence = () => {
    SoundManager.playSound('toggle');
    setTimeout(() => {
      setDungeonState(prev => ({
        ...prev,
        currentQuestionIndex: 0,
        phase: 'question'
      }));
      if (sceneRef.current) {
        createQuestionScene(sceneRef.current, 0);
      }
      animateText(questions[0].text, () => { setShowChoices(true); });
    }, 2000);
  };

  // Initialize
  useEffect(() => {
    if (!containerRef.current) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0804);
    scene.fog = new THREE.FogExp2(0x0a0804, 0.012);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      75, containerRef.current.clientWidth / containerRef.current.clientHeight, 0.1, 1000
    );
    camera.position.set(0, 2, 10);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const ambientLight = new THREE.AmbientLight(0x1a1008, 0.8);
    scene.add(ambientLight);
    const dirLight = new THREE.DirectionalLight(0xffd700, 0.4);
    dirLight.position.set(0, 10, 5);
    scene.add(dirLight);

    createIntroScene(scene);
    animateText("THE TEMPLE AWAITS YOUR PRESENCE...", () => {
      setTimeout(startSequence, 2000);
    });

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();

    const handleResize = () => {
      if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cleanupFnsRef.current.forEach(fn => fn());
      cleanupFnsRef.current = [];
      if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
      if (textAnimationRef.current !== null) cancelAnimationFrame(textAnimationRef.current);
      if (containerRef.current && rendererRef.current) {
        if (containerRef.current.contains(rendererRef.current.domElement)) {
          containerRef.current.removeChild(rendererRef.current.domElement);
        }
      }
      if (sceneRef.current && rendererRef.current) {
        disposeScene(sceneRef.current, rendererRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (dungeonState.phase === 'question' && dungeonState.currentQuestionIndex >= 0) {
      if (sceneRef.current) {
        createQuestionScene(sceneRef.current, dungeonState.currentQuestionIndex);
      }
      const qi = dungeonState.currentQuestionIndex;
      if (qi < questions.length) {
        animateText(questions[qi].text, () => { setShowChoices(true); });
      }
    }
  }, [dungeonState.phase, dungeonState.currentQuestionIndex]);

  useEffect(() => {
    return () => {
      if (textAnimationRef.current !== null) cancelAnimationFrame(textAnimationRef.current);
    };
  }, []);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="relative flex-grow" ref={containerRef}>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center p-4">
            <div className="text-2xl mb-4 font-bold" style={{ color: '#ffd700', textShadow: '0 0 10px #ffbf00, 0 0 20px #ff8c00' }}>
              {!conclusion && displayText}
            </div>

            {showChoices && dungeonState.currentQuestionIndex >= 0 && dungeonState.currentQuestionIndex < questions.length && (
              <div className="flex flex-col space-y-4 mt-8 pointer-events-auto">
                {questions[dungeonState.currentQuestionIndex].choices.map((choice, index) => (
                  <button
                    key={choice.id}
                    className="px-4 py-2 border-2 bg-black bg-opacity-70 hover:bg-opacity-90 font-serif"
                    style={{
                      color: '#ffd700',
                      borderColor: index === 0 ? '#ffd700' : index === 1 ? '#ffbf00' : '#ff8c00',
                      textShadow: '0 0 5px #ffbf00',
                      boxShadow: `0 0 8px ${index === 0 ? '#ffd70044' : index === 1 ? '#ffbf0044' : '#ff8c0044'}`
                    }}
                    onClick={() => selectChoice(dungeonState.currentQuestionIndex, index)}
                  >
                    {choice.text}
                  </button>
                ))}
              </div>
            )}

            {conclusion && (
              <div className="bg-black bg-opacity-80 p-6 max-w-xl mx-auto border border-[#ffd700]" style={{ boxShadow: '0 0 20px #ffd70033' }}>
                <p className="text-[#ffd700] whitespace-pre-line font-serif text-lg" style={{ textShadow: '0 0 8px #ffbf00' }}>
                  {displayText}
                </p>
              </div>
            )}
          </div>
        </div>

        {dungeonState.currentQuestionIndex >= 0 && !conclusion && (
          <div className="absolute bottom-4 left-4 bg-black bg-opacity-70 p-3 rounded border border-[#ffd700]">
            <p className="text-sm font-serif" style={{ color: '#ffd700' }}>
              <span className="font-bold">CHAMBER {dungeonState.currentQuestionIndex + 1} OF 5</span>
              {dungeonState.phase === 'transition' && (
                <span className="ml-2">
                  DESCENDING... {Math.round(dungeonState.transitionProgress * 100)}%
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dungeon2Visualization;
